import {
  Component,
  OnInit,
  ViewChild,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  Injector,
  Type,
  InjectionToken,
  AbstractType,
} from '@angular/core';
import { XuxEditGridCustomComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget-custom.component';
import { I18n } from '@app/i18n/strings-keys';
import { ICommissionRate } from '@app/shared/types/entities/hawker-profile/hawker-commission-rate.types';
import {
  BUTTON_FLEX_ORDER,
  CELL_RENDERER,
  COMMON_ACTION_BUTTONS,
  ENTITY_TYPES,
  IApiListResponse,
  IBaseEntityType,
  IModalDialogResult,
  IWidgetData,
  NotificationType,
  TOOLBAR_BUTTON_POSITION,
  XUX_GRID_ACTIONS,
  XuxGridAction,
  GridRowData,
  IHawkerProfile,
  COMMON_ACTION_TYPES,
} from '@app/shared/types';
import { BTN_REMOVE } from '@app/text-content/button-templates';
import { XuxGridCustomComponent } from '@components/xux-grid-custom';
import { GridApi, ICellRendererParams, RowNode } from 'ag-grid-community';
import {
  cloneDeep,
  eq,
  find,
  get,
  isFunction,
  map,
  size,
  merge,
  omit,
  filter,
  isEmpty,
  head,
} from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { XuxEditGridComponent } from '../xux-edit-grid/xux-edit-grid-widget.component';
import { lazyInject } from '@app/containers/base/decorators';
import {
  HawkerProfileService,
  XdmCommissionRateService,
} from '@app/shared/services';
import { APIActions, UiActions } from '@app/store/actions';
import { AgGridService } from '@app/shared/services/ag-grid';
import { XVII18nService } from '@app/i18n/services';
import { observableIIF, rx } from '@app/shared/rx';
import { ValidatorFn, FormControl } from '@angular/forms';
import {
  COLUMN_SELECTOR_ID,
  FieldsToKeepInDocumentAfterUpdateEnum,
} from '@config/index';
import { commissionRateRulesModalDefinition } from '@app/shared/services/xvi-commission-rate/commission-rate-rules-modal.definition';
import { getObservableValue } from '@app/shared/utils';
import { detectMobile } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import {
  TOASTER_ERROR_MESSAGE,
  TOASTER_SAVED_SUCCESSFULLY_MESSAGE,
  TOASTER_SUCCESS_MESSAGE,
} from '@app/shared/constants';
import { sortedCommissionRates } from '@app/shared/services/hawker-profile/hawker-profile.utils';
import { DEFAULT_GRID_MIN_HEIGHT } from '@config/global-constants';

@Component({
  selector: 'xvi-commission-rate-assignment-grid-widget',
  templateUrl: './commission-rate-assignment-grid-widget.component.html',
  styleUrls: ['./commission-rate-assignment-grid-widget.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommissionRateAssignmentGridWidgetComponent
  extends XuxEditGridCustomComponent
  implements OnInit
{
  @ViewChild(XuxGridCustomComponent, { static: true })
  public xuxGridComponent: XuxGridCustomComponent;
  @lazyInject(HawkerProfileService)
  public hawkerProfileService: HawkerProfileService;
  @lazyInject(XdmCommissionRateService)
  public xdmCommissionRateService: XdmCommissionRateService;

  @lazyInject(APIActions)
  public apiActions: APIActions;

  public hawkerProfile: IHawkerProfile;
  public documentId: string = '';
  public employeeId: string = '';
  public widgetLoader$ = new BehaviorSubject<boolean>(true);
  public canSaveObs$ = new BehaviorSubject<boolean>(false);
  public isUsePatchToSaveObs$ = new BehaviorSubject<boolean>(false);
  public handleAssignmentGridSave$ = new BehaviorSubject<boolean>(false);
  public isAssignmentGridChange$ = new BehaviorSubject<boolean>(false);
  public descriptionObs$ = new BehaviorSubject<string>(
    this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
      count: 0,
      entities: this.i18nService.translate(I18n.Common.RateWithBracket),
    }),
  );
  public defaultWidgetData: IWidgetData = {};
  public widgetData: IWidgetData = {};
  public initXuxGridItems: ICommissionRate[] = [];
  public employeeFullName: string = '';

  private gridApi: GridApi | null = null;
  private gridComponent: XuxEditGridComponent | null = null;

  public constructor(
    public uiActions: UiActions,
    public agGridService: AgGridService,
    public i18nService: XVII18nService,
    public injector: Injector,
  ) {
    super(uiActions, agGridService, i18nService);
  }

  public ngOnInit(): void {
    super.ngOnInit();

    this.initDefaultData();
  }

  public inject<T>(
    token: Type<T> | InjectionToken<T> | AbstractType<T>,
    notFoundValue?: T,
  ): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public getGridReadyHandler(
    action: XuxGridAction,
    gridComponent?: XuxEditGridComponent,
  ): void {
    if (gridComponent) {
      this.gridComponent = gridComponent;
    }

    if (action.payload?.gridApi) {
      this.gridApi = action.payload.gridApi;
    }
  }

  private createCommissionRateValidators(): ValidatorFn[] {
    const primaryRateValidator = (control: FormControl) => {
      const rates = control?.value as ICommissionRate[];
      if (rates && Array.isArray(rates) && rates.length > 0) {
        const hasPrimary = this.hasPrimaryRate(rates);
        if (!hasPrimary) {
          this.canSaveObs$.next(false);

          return {
            noPrimaryRate:
              I18n.Forms.HawkerProfileForm.PrimaryRateRequiredError,
          };
        }
      }

      return null;
    };

    return [primaryRateValidator];
  }

  private hasPrimaryRate(rates: ICommissionRate[]): boolean {
    return rates.some((rate) => eq(rate.is_primary, true));
  }

  public handleGridColumns(): void {
    super.handleGridColumns();

    if (this.gridColumns) {
      this.gridColumns = this.gridColumns.map((column) => {
        if (column.path === 'name') {
          column.cellClass = `${column.cellClass || ''} commission-rate-name-cell`;
        }

        if (column.path === 'is_primary') {
          column.cellClass = `${column.cellClass || ''} commission-rate-primary-cell`;
        }

        return column;
      });
    }
  }

  public onSwitchChange = (
    isPrimary: boolean,
    rowData: GridRowData,
    params: ICellRendererParams,
  ) => {
    const gridApi = params.api;
    this.setPrimaryRate(isPrimary, rowData.entity_id);
    gridApi.setRowData([...this.initXuxGridItems]);
    this.setValue(this.initXuxGridItems);
  };

  public async assignSelectedRate(): Promise<void> {
    const excludeCommissionRateIds = map(this.initXuxGridItems, 'entity_id');
    this.uiActions.enableLoader();
    const ratesRes = await this.xdmCommissionRateService
      .fetchList({ select: ['entity_id'] })
      .toPromise()
      .finally(() => {
        this.uiActions.disableLoader();
      });
    // available commission rates
    const availableRates = filter(
      get(ratesRes, 'items', []),
      (rate) => !excludeCommissionRateIds.includes(rate.entity_id),
    );
    const modal = commissionRateRulesModalDefinition.commissionRateRulesModal(
      this.hawkerProfileService.xviCommissionRateService,
      {
        excludeCommissionRateIds,
        employeeFullName: this.employeeFullName,
        isNoCommissionRate: isEmpty(availableRates),
      },
    );
    const res = await this.uiActions.promptFormModal(modal);

    if (!res.confirmed) {
      return;
    }
    const updateRates = this.formatModalData(res);
    this.setValue(updateRates);

    return;
  }

  public getSelectedNodes(): RowNode[] {
    if (!this.gridApi) {
      return [];
    }

    return this.gridApi.getSelectedNodes();
  }

  public removeSelectedRates(): void {
    const selectedNodes = this.getSelectedNodes();
    if (selectedNodes.length === 0) {
      return;
    }

    const selectedData = selectedNodes.map((node) => node.data);
    const selectedIds = selectedData.map((data) => data.entity_id);

    this.initXuxGridItems = this.initXuxGridItems.filter(
      (rate) => !selectedIds.includes(rate.entity_id),
    );
    this.setValue(this.initXuxGridItems);

    if (this.gridApi) {
      this.gridApi.setRowData([...this.initXuxGridItems]);

      this.gridApi.deselectAll();
      this.gridApi?.refreshCells({ force: true });
    }
  }

  protected getWidgetDataDefaults(): IWidgetData {
    const canManage = this.hawkerProfileService.canManage;

    const defaultWidgetData = {
      gridHeight: DEFAULT_GRID_MIN_HEIGHT,
      hiddenErrorBlock: true,
      useVisibleItemCountDescription: true,
      filterable: true,
      showSearch: true,
      multiselect: canManage,
      orderControls: false,
      disableColumnSelector: true,
      valueField: FieldsToKeepInDocumentAfterUpdateEnum.ENTITY_ID,
      userGridOptions: {
        suppressMovableColumns: true,
        suppressClickEdit: true,
        suppressRowClickSelection: false,
        rowSelection: 'multiple',
        defaultColDef: {
          editable: false,
        },
        overlayNoRowsTemplate: this.i18nService.instant(
          I18n.Common.NoRowsToShow,
        ),
      },
      columns: [
        {
          path: 'name',
          title: I18n.EntityNames.CommissionRate,
          cellClass: 'cell-wrap-text',
          autoHeight: true,
          defaultView: true,
          sortable: false,
          filterParams: {
            quickFilter: true,
          },
          cellRenderer: CELL_RENDERER.link_renderer,
          cellRendererParams: {
            onClick: (data: ICommissionRate): void => {
              this.hawkerProfileService.openCommissionRateRuleModal({
                commissionRateId: data.entity_id,
                excludeCommissionRateIds: [],
                rateName: data.name,
              });
            },
          },
          minWidth: 300,
          headerCheckboxSelection: false,
        },
        {
          path: 'is_primary',
          title: I18n.Forms.HawkerProfileForm.PrimaryRate,
          defaultView: true,
          cellRenderer: CELL_RENDERER.switch_radio_renderer,
          cellRendererParams: {
            onValueChange: this.onSwitchChange,
            disabled: !canManage,
          },
          minWidth: 120,
          width: 120,
        },
        {
          path: COLUMN_SELECTOR_ID,
          width: 50,
          maxWidth: 50,
          title: '',
          defaultView: true,
          cellRenderer: CELL_RENDERER.common_actions_renderer,
          cellRendererParams: {
            entityType: ENTITY_TYPES.HAWKER_PROFILE,
            actionButtons: [
              {
                ...COMMON_ACTION_BUTTONS.Remove,
                value: 'assignment-remove',
                type: COMMON_ACTION_TYPES.AssignmentRemove,
                permissionCodes: ['VenuesInventory.HawkerProfile.Manage'],
                click: () => this.removeSelectedRates(),
              },
            ],
          },
          pinned: detectMobile() ? false : 'right',
          lockPinned: true,
        },
      ],
      buttons: [
        {
          caption: I18n.Forms.HawkerProfileForm.AssignCommissionRate,
          class: 'btn btn-default btn-primary',
          flexOrder: BUTTON_FLEX_ORDER.RIGHTMOST,
          disabledObs: new BehaviorSubject<boolean>(!canManage),
          position: TOOLBAR_BUTTON_POSITION.HEADER,
          click: async (): Promise<void> => {
            await this.assignSelectedRate();
          },
        },
        {
          caption: BTN_REMOVE.caption,
          value: 'custom-remove-rates',
          flexOrder: BUTTON_FLEX_ORDER.LEFTMOST,
          click: () => {
            this.removeSelectedRates();
          },
          hidden: (selectedItems): boolean =>
            !canManage || selectedItems.length < 1,
        },
      ],
      eventHandlers: {
        [XUX_GRID_ACTIONS.GRID_READY]: (
          action: XuxGridAction,
          grid: XuxEditGridComponent,
        ): void => this.getGridReadyHandler(action, grid),
      },
    };

    return merge({}, defaultWidgetData, this.field.widgetData, {
      columns: get(
        this.field,
        ['widgetData', 'columns'],
        defaultWidgetData.columns,
      ),
    });
  }

  public initDefaultData(): void {
    this.handleAssignmentGridSave$ = get(
      this.widgetData,
      'handleAssignmentGridSaveObs',
      this.handleAssignmentGridSave$,
    );
    this.canSaveObs$ = get(this.widgetData, 'canSaveObs', this.canSaveObs$);
    this.isAssignmentGridChange$ = get(
      this.widgetData,
      'isAssignmentGridChangeObs',
      this.isAssignmentGridChange$,
    );
    this.employeeId = get(this.widgetData, 'employee_id', this.employeeId);
    this.field.control.setValidators(this.createCommissionRateValidators());

    this.widgetLoader$ = get(
      this.field,
      'widgetData.showLoader',
      this.widgetLoader$,
    );
    if (this.employeeId) {
      this.setGridData(this.employeeId);
    }
    this.initSaveDescription();
  }

  public setValue(value: ICommissionRate[], isInit: boolean = false): void {
    if (isInit) {
      this.field.control.setValue(value);
      this.initXuxGridItems = value;
      this.field.control.markAsPristine();
      this.updateDescription(size(value));

      return;
    }

    if (this.field?.control) {
      this.initXuxGridItems = value;
      this.updateDescription(size(value));
      this.field.control.setValue(value);
      this.field.control.markAsDirty();
      this.field.control.markAsTouched();
      this.field.control.updateValueAndValidity();
      this.canSaveObs$.next(this.field.control.valid);
      this.isAssignmentGridChange$.next(true);
    }
  }

  public setGridData(employeeId: string): void {
    this.widgetLoader$.next(true);
    this.subscriptions.push(
      this.hawkerProfileService
        .fetchHawkerProfileList({
          filter: `employee_id eq in(${employeeId})`,
        })
        .pipe(
          rx.tap((resp: IApiListResponse<IBaseEntityType>) => {
            const matchedDoc = find(get(resp, 'items'), (item) =>
              eq(item.employee_id, employeeId),
            );
            // when employee has not saved commission rate yet. the matchedDoc will be undefined.
            this.initXuxGridItems = get(matchedDoc, 'commission_rates', []);
            this.documentId = get(matchedDoc, '_id', '');
            this.hawkerProfile = {
              employee_id: employeeId,
              ...matchedDoc,
            } as IHawkerProfile;

            const isPatch = !!this.documentId;
            this.isUsePatchToSaveObs$.next(isPatch);

            this.setGridRowData(this.initXuxGridItems);
            this.setValue(this.initXuxGridItems, true);
            this.widgetLoader$.next(false);
          }),
        )
        .subscribe(),
      this.hawkerProfileService.bohDataService
        .getEmployeeByIds([employeeId])
        .pipe(
          rx.tap(({ items }) => {
            const hawkerProfile = this.hawkerProfileService.formatDocument(
              head(items),
            );
            this.employeeFullName =
              this.hawkerProfileService.getEmployeeFullName(hawkerProfile);
          }),
        )
        .subscribe(),
    );
  }

  public commissionRateSaveHandler(): void {
    this.uiActions.enableLoader();

    const sortedRates: ICommissionRate[] = sortedCommissionRates(
      this.field.control.value,
    );

    observableIIF(
      () => getObservableValue(this.isUsePatchToSaveObs$),
      this.hawkerProfileService.updateEmployeeCommissionRate(
        this.documentId,
        sortedRates,
      ),
      this.hawkerProfileService.saveEmployeeCommissionRate(
        this.employeeId,
        sortedRates,
      ),
    )
      .pipe(
        rx.tap((response) => {
          const documentId = get(response, '_id');
          if (documentId) {
            this.documentId = documentId;
            this.isUsePatchToSaveObs$.next(true);
          }

          // update store item
          this.apiActions.storeUpdatedItem(ENTITY_TYPES.HAWKER_PROFILE, {
            ...omit(this.hawkerProfile, '_id'),
            entity_id: this.hawkerProfile.employee_id,
            commission_rates: sortedRates,
          });
          this.canSaveObs$.next(false);
        }),
        rx.catchError((error) => {
          this.uiActions.showToasterMessage({
            type: NotificationType.ERROR,
            title: TOASTER_ERROR_MESSAGE.title,
          });
          this.resetGridStatus();
          this.uiActions.disableLoader();

          return error;
        }),
      )
      .subscribe(() => {
        this.resetGridStatus();
        this.setValue(sortedRates, true);
        this.uiActions.disableLoader();
        this.uiActions.showToasterMessage({
          type: NotificationType.SUCCESS,
          title: TOASTER_SUCCESS_MESSAGE.title,
          body: TOASTER_SAVED_SUCCESSFULLY_MESSAGE,
        });
      });
  }

  public formatModalData(modalData: IModalDialogResult): ICommissionRate[] {
    let newCommissionRate: ICommissionRate = {
      entity_id: get(modalData, ['value', 'commission_rate_id']),
      name: get(modalData, ['value', 'virtual', 'commission_rate_name']),
      is_primary: false,
    };
    const initXuxGridItems = cloneDeep(this.initXuxGridItems);
    const hasPrimaryRateOn = this.hasPrimaryRate(initXuxGridItems);
    if (!hasPrimaryRateOn) {
      newCommissionRate = {
        ...newCommissionRate,
        is_primary: !hasPrimaryRateOn,
      };
    } else {
      newCommissionRate = {
        ...newCommissionRate,
        is_primary: !hasPrimaryRateOn,
      };
    }
    initXuxGridItems.push(newCommissionRate);

    return initXuxGridItems;
  }

  public resetGridStatus(): void {
    this.isAssignmentGridChange$.next(false);
    this.handleAssignmentGridSave$.next(false);
  }

  public updateDescription(count: number): void {
    this.descriptionObs$.next(
      this.i18nService.instant(I18n.Forms.GridMessages.ShowingEntities, {
        count,
        entities: this.i18nService.translate(I18n.Common.RateWithBracket),
      }),
    );
  }

  public initSaveDescription(): void {
    this.subscriptions.push(
      this.handleAssignmentGridSave$
        .pipe(rx.distinctUntilChanged())
        .subscribe((confirmSave: boolean): void => {
          if (!confirmSave) {
            return;
          }
          this.commissionRateSaveHandler();
        }),
    );
  }

  public setPrimaryRate(isPrimary: boolean, RowEntityId: string): void {
    this.initXuxGridItems = this.initXuxGridItems.map((rate) => ({
      ...rate,
      is_primary: isPrimary ? rate.entity_id === RowEntityId : false,
    }));
  }
}
